<script>
import { minLength, maxLength } from "@vuelidate/validators";
import { dateIsMajeur, birthdate, telMobile } from "@/utils/form-validators";
import { isCarteCora } from "@/utils/fidelite";

import Button from "@/components/atoms/button";
import FormStep from "@/components/molecules/form-step";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import FormRadio from "@/components/molecules/form-radio";
import FormCheckbox from "@/components/molecules/form-checkbox";
import Titre from "@/components/atoms/titre";
import LazyImage from "@/components/atoms/lazy-image";
import FormError from "@/components/molecules/form/form-error";

import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import CarteFideliteTooltip from "@/components/organisms/carte-fidelite-form/tooltip";
import CarteFideliteRetrouverPorteur from "@/components/organisms/carte-fidelite-form/retrouver-porteur";
import CmsFetcher from "@/containers/cms-fetcher";
import Popin from "@/containers/popin-adaptive";
import MentionsLegalesNewsletter from "@/components/molecules/mentions-legales/newsletter";
import MentionsLegalesFidelite from "@/components/molecules/mentions-legales/fidelite";
import carteCora from "@/assets/images/carte-cora.png";
import carteMalinSeule from "@/assets/images/carte-malin-seule.png";
import carteMalin from "@/assets/images/carte-malin.png";

export default {
  name: "CreationCarteFidForm",
  components: {
    Form,
    FormStep,
    FormGroup,
    FormInput,
    FormRadio,
    FormCheckbox,
    Button,
    CarteFideliteTooltip,
    CarteFideliteRetrouverPorteur,
    CmsFetcher,
    Popin,
    MentionsLegalesNewsletter,
    MentionsLegalesFidelite,
    Titre,
    LazyImage,
    FormError
  },
  props: {
    isEligibleDefis: {
      type: Boolean,
      required: false,
      default: false
    },
    tel: {
      type: String,
      required: false,
      default: null
    },
    isErrorCarteAssociee: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  emits: ["addCarte", "createCarte"],
  setup(props) {
    const {
      v$,
      getState,
      getErrorMessage,
      scrollToFirstError,
      checkValidation,
      isLoadingComputed
    } = useFormValidation(props);
    return {
      carteMalinSeule,
      carteMalin,
      carteCora,
      v$,
      getState,
      getErrorMessage,
      scrollToFirstError,
      checkValidation,
      isLoadingComputed
    };
  },
  data() {
    return {
      ajoutCarte: {
        nom_porteur_carte: "",
        prenom_porteur_carte: "",
        num_carte: ""
      },
      creationCarte: {
        dateNaissance: "",
        enfants: "",
        dateNaissanceEnfant1: "",
        dateNaissanceEnfant2: "",
        dateNaissanceEnfant3: "",
        dateNaissanceEnfant4: ""
      },
      optin_newsletter_sms: "",
      cgvLink: "/legal/informations-juridiques/conditions-generales-fidelite",
      choixCarteFidelite: "",
      isCgvFidChecked: false,
      enfants: false,
      nbEnfants: 1,
      isStepFideliteValidated: false
    };
  },
  computed: {
    showNewsletterSms() {
      return this.tel && telMobile(this.tel);
    },
    typeNumCarte() {
      if (
        this.v$.ajoutCarte.num_carte.$invalid ||
        this.ajoutCarte.num_carte === ""
      ) {
        return null;
      } else if (isCarteCora(this.ajoutCarte.num_carte)) {
        return "cora";
      } else {
        return "malin";
      }
    },
    isSubmitButtonDisabled() {
      return (
        !this.isCgvFidChecked ||
        (this.v$.optin_newsletter_sms.$invalid && this.showNewsletterSms)
      );
    }
  },
  validations() {
    return {
      ajoutCarte: {
        num_carte: {
          minLength: minLength(13),
          maxLength: maxLength(13),
          required() {
            return true;
          },
          requiredIf(value) {
            if (this.choixCarteFidelite === "carte-existante") {
              return value !== "";
            }
            return true;
          }
        },
        nom_porteur_carte: {
          minLength: minLength(1),
          maxLength: maxLength(355),
          required() {
            return true;
          },
          requiredIf(value) {
            if (this.choixCarteFidelite === "carte-existante") {
              return value !== "";
            }
            return true;
          }
        },
        prenom_porteur_carte: {
          minLength: minLength(1),
          maxLength: maxLength(355),
          required() {
            return true;
          },
          requiredIf(value) {
            if (this.choixCarteFidelite === "carte-existante") {
              return value !== "";
            }
            return true;
          }
        }
      },
      creationCarte: {
        dateNaissance: {
          required() {
            return true;
          },
          requiredIf(value) {
            if (this.choixCarteFidelite === "nouvelle-carte") {
              return value !== "";
            }
            return true;
          },
          dateIsMajeur
        },
        dateNaissanceEnfant1: {
          birthdate
        },
        dateNaissanceEnfant2: {
          birthdate
        },
        dateNaissanceEnfant3: {
          birthdate
        },
        dateNaissanceEnfant4: {
          birthdate
        },
        enfants: {
          requiredIf(value) {
            if (this.choixCarteFidelite === "nouvelle-carte") {
              return value !== "";
            }
            return true;
          }
        }
      },
      optin_newsletter_sms: {
        requiredIf(value) {
          if (
            this.choixCarteFidelite === "nouvelle-carte" &&
            this.showNewsletterSms
          ) {
            return value !== "";
          }
          return true;
        }
      }
    };
  },
  methods: {
    clearFideliteInfos() {
      this.ajoutCarte = {
        nom_porteur_carte: "",
        prenom_porteur_carte: "",
        num_carte: ""
      };
      this.creationCarte = {
        dateNaissance: "",
        enfants: "",
        dateNaissanceEnfant1: "",
        dateNaissanceEnfant2: "",
        dateNaissanceEnfant3: "",
        dateNaissanceEnfant4: ""
      };
    },
    save() {
      if (this.checkValidation(this.v$)) {
        if (this.choixCarteFidelite === "carte-existante") {
          this.$emit("addCarte", this.constructAjoutCarte());
        } else {
          this.$emit("createCarte", this.constructCreationCarte());
        }
      }
    },
    constructAjoutCarte() {
      if (isCarteCora(this.ajoutCarte.num_carte)) {
        return {
          num_carte_cora: this.ajoutCarte.num_carte,
          nom_porteur_carte_cora: this.ajoutCarte.nom_porteur_carte,
          prenom_porteur_carte_cora: this.ajoutCarte.prenom_porteur_carte
        };
      } else {
        return {
          num_carte_malin: this.ajoutCarte.num_carte,
          nom_porteur_carte_malin: this.ajoutCarte.nom_porteur_carte,
          prenom_porteur_carte_malin: this.ajoutCarte.prenom_porteur_carte
        };
      }
    },
    constructCreationCarte() {
      const dateNaissanceEnfants = [];
      if (this.creationCarte.dateNaissanceEnfant1) {
        dateNaissanceEnfants.push(this.creationCarte.dateNaissanceEnfant1);
      }
      if (this.creationCarte.dateNaissanceEnfant2) {
        dateNaissanceEnfants.push(this.creationCarte.dateNaissanceEnfant2);
      }
      if (this.creationCarte.dateNaissanceEnfant3) {
        dateNaissanceEnfants.push(this.creationCarte.dateNaissanceEnfant3);
      }
      if (this.creationCarte.dateNaissanceEnfant4) {
        dateNaissanceEnfants.push(this.creationCarte.dateNaissanceEnfant4);
      }
      return {
        dateNaissance: this.creationCarte.dateNaissance,
        acceptRelance: true,
        acceptSms: this.showNewsletterSms ? this.optin_newsletter_sms : false,
        acceptEmail: false,
        dateNaissanceEnfants
      };
    },
    isCreationCarteValid() {
      return !this.v$.creationCarte.$invalid;
    },
    validateCreationCarte() {
      this.v$.creationCarte.$touch();
      if (this.v$.creationCarte.$invalid) {
        this.scrollToFirstError();
      } else {
        this.isStepFideliteValidated = true;
      }
    },
    imageFormCarte(typeNumCarte) {
      if (!typeNumCarte) {
        return null;
      } else if (typeNumCarte === "cora") {
        return carteCora;
      } else {
        return carteMalin;
      }
    }
  }
};
</script>

<template>
  <Form
    class="c-inscription-form"
    @submit.prevent="save()"
  >
    <div class="c-inscription-form__title c-inscription-form__title--fid u-mb-md@md">
      <Titre
        tag="h1"
        size="xlg"
        variant="primary"
        class="u-mb-lg20 u-mb-xxs@md u-text-center@md"
      >
        Ma carte de fidélité
      </Titre>
      <LazyImage
        :src="carteMalinSeule"
        width="85"
        height="64"
        class="u-none u-block@md"
      />
    </div>
    <FormStep
      ref="fidelite"
      icon-name="fidelity"
      legend="Possédez-vous une carte de fidélité Cora ?"
      title
      class="u-full-width"
    >
      <div class="c-inscription-form__step-content">
        <FormGroup>
          <FormRadio
            v-model="choixCarteFidelite"
            name="carte-fidelite"
            value="carte-existante"
            class="u-font-14"
            @change="clearFideliteInfos"
          >
            J'ai déjà une carte
          </FormRadio>
          <FormRadio
            v-model="choixCarteFidelite"
            name="carte-fidelite"
            value="nouvelle-carte"
            class="u-font-14 u-mt-sm"
            @change="clearFideliteInfos"
          >
            Je créé ma carte gratuitement
          </FormRadio>
        </FormGroup>

        <div
          v-if="choixCarteFidelite === 'carte-existante'"
          class="u-mt-lg20"
        >
          <h2 class="u-font-19 u-mb-lg">
            Enregistrez-la pour consulter et profiter de vos <br class="u-none@md">
            avantages à tout moment !
          </h2>
          <FormGroup
            class="u-my-sm"
            :state="getState(v$,'num_carte_' + typeNumCarte) || getState(v$,'num_carte', 'ajoutCarte')"
          >
            <FormInput
              id="numero"
              name="numero"
              v-model.trim="ajoutCarte.num_carte"
              maxlength="13"
              autocapitalize="off"
              aria-describedby="numeroCarteMessage"
              :img-path="imageFormCarte(typeNumCarte)"
              :state="getState(v$,'num_carte_' + typeNumCarte) || getState(v$,'num_carte', 'ajoutCarte')"
              @blur="v$.ajoutCarte.num_carte.$touch()"
              @input="v$.ajoutCarte.num_carte.$reset()"
            >
              N° de fidélité
            </FormInput>
            <template #message>
              <div id="numeroCarteMessage">
                <p>
                  {{ getErrorMessage('num_carte_' + typeNumCarte) }}
                  <template v-if="v$.ajoutCarte.num_carte.minLength.$invalid || v$.ajoutCarte.num_carte.maxLength.$invalid">
                    Veuillez entrer un numéro de carte valide : il doit contenir 13 chiffres
                  </template>
                </p>
              </div>
            </template>
          </FormGroup>

          <CarteFideliteTooltip />

          <FormGroup
            class="u-my-sm"
            :state="getState(v$,'nom_porteur_carte_' + typeNumCarte) || getState(v$,'nom_porteur_carte', 'ajoutCarte')"
          >
            <FormInput
              id="nomPorteur"
              v-model.trim="v$.ajoutCarte.nom_porteur_carte.$model"
              name="nom"
              aria-describedby="nomPorteurCarteMessage"
              :state="getState(v$,'nom_porteur_carte_' + typeNumCarte) || getState(v$,'nom_porteur_carte', 'ajoutCarte')"
              @blur="v$.ajoutCarte.nom_porteur_carte.$touch()"
              @input="v$.ajoutCarte.nom_porteur_carte.$reset()"
            >
              Nom du porteur
            </FormInput>
            <template #message>
              <div id="nomPorteurCarteMessage">
                <p>
                  {{ getErrorMessage('nom_porteur_carte_' + typeNumCarte) }}
                  <template v-if="v$.ajoutCarte.nom_porteur_carte.$invalid">
                    Veuillez entrer le nom du porteur de la carte de fidélité
                  </template>
                </p>
              </div>
            </template>
          </FormGroup>

          <FormGroup
            class="u-my-sm"
            :state="getState(v$,'prenom_porteur_carte_' + typeNumCarte) || getState(v$,'prenom_porteur_carte', 'ajoutCarte')"
          >
            <FormInput
              id="prenomPorteur"
              v-model.trim="ajoutCarte.prenom_porteur_carte"
              name="prenom"
              aria-describedby="prenomPorteurCarteMessage"
              :state="getState(v$,'prenom_porteur_carte_' + typeNumCarte) || getState(v$,'prenom_porteur_carte', 'ajoutCarte')"
              @blur="v$.ajoutCarte.prenom_porteur_carte.$touch()"
              @input="v$.ajoutCarte.prenom_porteur_carte.$reset()"
            >
              Prénom du porteur
            </FormInput>
            <template #message>
              <div id="prenomPorteurCarteMessage">
                <p>
                  {{ getErrorMessage('prenom_porteur_carte_' + typeNumCarte) }}
                </p>
                <template v-if="v$.ajoutCarte.prenom_porteur_carte.$invalid">
                  Veuillez entrer le prénom du porteur de la carte de fidélité
                </template>
              </div>
            </template>
          </FormGroup>

          <CarteFideliteRetrouverPorteur :is-eligible-defis="isEligibleDefis" />
          <div
            v-if="error"
            class="c-inscription-form__submit-error u-text-center u-mt-md"
          >
            <FormError :error="error" />
            <Button
              v-if="isErrorCarteAssociee"
              to="/infos-et-services/pages-cachees/carte-fidelite-associee-a-un-compte-comment-faire"
              target="_blank"
              link
            >Aide</Button>
          </div>
          <Button
            variant="secondary"
            radius="full"
            size="md"
            class="u-mt-md u-ml-lg u-flex-justify-center@sm"
            type="submit"
            :disabled="v$.ajoutCarte.$invalid || isLoadingComputed"
            value="Je valide mes informations"
          />
        </div>
        <div
          v-else-if="choixCarteFidelite === 'nouvelle-carte'"
          class="u-mt-lg20"
        >
          <h2 class="u-font-19 u-mb-lg">
            Dites-nous en plus sur vous
          </h2>
          <FormGroup
            :state="getState(v$,'dateNaissance', 'creationCarte')"
            class="c-inscription-form__form-group"
          >
            <FormInput
              id="dateNaissance"
              v-model.trim="creationCarte.dateNaissance"
              type="date"
              name="dateNaissance"
              active
              :state="getState(v$,'dateNaissance', 'creationCarte')"
              aria-describedby="dateNaissance"
              @blur="v$.creationCarte.dateNaissance.$touch()"
              @input="v$.creationCarte.dateNaissance.$touch()"
            >
              Votre date de naissance *
            </FormInput>
            <template #message>
              <div id="dateNaissance">
                <p>
                  {{ getErrorMessage("dateNaissance", "Le porteur de carte doit être une personne majeure") }}
                </p>
              </div>
            </template>
          </FormGroup>
          <h2 class="u-font-19 u-mt-lg20 u-mb-xs">
            Avez-vous des enfants ?*
          </h2>
          <p class="u-mb-md">
            Grâce à cette information, nous pourrons vous proposer <br class="u-none@md">
            des offres et avantages adaptés à votre famille
          </p>
          <FormGroup inline>
            <FormRadio
              v-model.trim="creationCarte.enfants"
              name="enfants"
              :value="true"
              class="u-mr-lg"
            >
              Oui
            </FormRadio>
            <FormRadio
              v-model.trim="creationCarte.enfants"
              name="enfants"
              :value="false"
            >
              Non
            </FormRadio>
          </FormGroup>
          <template v-if="creationCarte.enfants">
            <FormGroup
              class="c-inscription-form__form-group u-mt-lg u-mb-sm"
              :state="getState(v$,'dateNaissanceEnfant1', 'creationCarte')"
            >
              <FormInput
                id="dateNaissanceEnfant1"
                v-model.trim="creationCarte.dateNaissanceEnfant1"
                type="date"
                name="dateNaissanceEnfant1"
                active
                :state="getState(v$,'dateNaissanceEnfant1', 'creationCarte')"
                aria-describedby="dateNaissanceEnfant1"
                @blur="v$.creationCarte.dateNaissanceEnfant1.$touch()"
                @input="v$.creationCarte.dateNaissanceEnfant1.$touch()"
              >
                Date de naissance de votre 1er enfant
              </FormInput>
            </FormGroup>
            <FormGroup
              v-show="(nbEnfants >= 2)"
              class="c-inscription-form__form-group u-mb-md"
              :state="getState(v$,'dateNaissanceEnfant2', 'creationCarte')"
            >
              <FormInput
                id="dateNaissanceEnfant2"
                v-model.trim="creationCarte.dateNaissanceEnfant2"
                type="date"
                name="dateNaissanceEnfant2"
                active
                :state="getState(v$,'dateNaissanceEnfant2', 'creationCarte')"
                aria-describedby="dateNaissanceEnfant2"
                @blur="v$.creationCarte.dateNaissanceEnfant2.$touch()"
                @input="v$.creationCarte.dateNaissanceEnfant2.$touch()"
              >
                Date de naissance de votre 2ème enfant
              </FormInput>
            </FormGroup>
            <FormGroup
              v-show="(nbEnfants >= 3)"
              class="c-inscription-form__form-group u-mb-md"
              :state="getState(v$,'dateNaissanceEnfant3', 'creationCarte')"
            >
              <FormInput
                id="dateNaissanceEnfant3"
                v-model.trim="creationCarte.dateNaissanceEnfant3"
                type="date"
                name="dateNaissanceEnfant3"
                active
                :state="getState(v$,'dateNaissanceEnfant3', 'creationCarte')"
                aria-describedby="dateNaissanceEnfant3"
                @blur="v$.creationCarte.dateNaissanceEnfant3.$touch()"
                @input="v$.creationCarte.dateNaissanceEnfant3.$touch()"
              >
                Date de naissance de votre 3ème enfant
              </FormInput>
            </FormGroup>

            <FormGroup
              v-show="(nbEnfants >= 4)"
              class="c-inscription-form__form-group u-mb-md"
              :state="getState(v$,'dateNaissanceEnfant4', 'creationCarte')"
            >
              <FormInput
                id="dateNaissanceEnfant4"
                v-model.trim="creationCarte.dateNaissanceEnfant4"
                type="date"
                name="dateNaissanceEnfant4"
                active
                :state="getState(v$,'dateNaissanceEnfant4', 'creationCarte')"
                aria-describedby="dateNaissanceEnfant4"
                @blur="v$.creationCarte.dateNaissanceEnfant4.$touch()"
                @input="v$.creationCarte.dateNaissanceEnfant4.$touch()"
              >
                Date de naissance de votre 4ème enfant
              </FormInput>
            </FormGroup>
            <Button
              v-if="nbEnfants < 4"
              variant="primary"
              outline
              radius="full"
              size="md"
              @click="() => { if(nbEnfants<4) { nbEnfants++ }}"
            >
              Ajouter un enfant
            </Button>
            <p v-else>
              Vous pouvez enregistrer un maximum de 4 enfants
            </p>
          </template>
          <div v-if="!isStepFideliteValidated">
            <Button
              variant="secondary"
              radius="full"
              size="md"
              class="u-mt-md u-ml-lg u-flex-justify-center@sm"
              :disabled="v$.creationCarte.$invalid"
              @click="() => {validateCreationCarte()}"
            >
              Je valide mes informations
            </Button>
          </div>
        </div>
      </div>
    </FormStep>
    <template v-if="choixCarteFidelite === 'nouvelle-carte'">
      <FormStep
        v-if="showNewsletterSms"
        ref="notifications"
        icon-name="notification"
        legend="Vos notifications"
        title
        class="u-full-width"
        :disabled="!isStepFideliteValidated"
      >
        <div
          v-show="isStepFideliteValidated"
          class="c-inscription-form__step-content"
        >
          <h2 class="u-font-19 u-mb-md">
            Souhaitez-vous recevoir les promotions et les offres Cora ?<Button
              link
              class="u-ml-xs"
              to="#juridiqueNewsletter"
            >
              (¹)
            </Button>
          </h2>

          <div
            v-if="showNewsletterSms"
            class="c-form-inscription__subcribe-promo"
          >
            <span class="c-form-inscription__subcribe-promo-label">par SMS *</span>
            <FormGroup
              :state="getState(v$,'optin_newsletter_sms')"
              inline
            >
              <FormRadio
                v-model.trim="optin_newsletter_sms"
                name="optin_newsletter_sms"
                :value="true"
                class="c-form-inscription__subcribe-promo-input"
                aria-describedby="optinNewsLetterMailMessage"
              >
                Oui
              </FormRadio>
              <FormRadio
                v-model.trim="optin_newsletter_sms"
                name="optin_newsletter_sms"
                :value="false"
                class="c-form-inscription__subcribe-promo-input"
                aria-describedby="optinNewsLetterMailMessage"
              >
                Non
              </FormRadio>
              <template #message>
                <div id="optinNewsLetterMailMessage">
                  <p>
                    {{ getErrorMessage("optin_newsletter_sms","Merci de renseigner votre choix") }}
                  </p>
                </div>
              </template>
            </FormGroup>
          </div>
        </div>
      </FormStep>
      <div v-if="isStepFideliteValidated">
        <FormCheckbox
          v-model="isCgvFidChecked"
          class="u-flex-self-start u-text-left u-mt-lg u-px-md@md"
        >
          <p class="u-color-grey-1 u-font-14">
            En cochant cette case, vous acceptez les
            <Button
              link
              class="c-inscription-form__cgv-button"
              @click="$refs.cgvPopin.show()"
            >
              Conditions Générales du programme de fidélité Cora
            </Button> *
          </p>
        </FormCheckbox>
        <div class="u-mt-lg20 u-pl-xlg u-pr-md u-pl-md@lg u-ml-xlg u-ml-lg@md">
          <Button
            variant="primary"
            radius="full"
            type="submit"
            value="Je valide mes informations"
            size="md"
            :disabled="(isLoadingComputed || isSubmitButtonDisabled)"
          />
        </div>
      </div>
    </template>

    <p class="c-inscription-form__required-field u-mt-lg30 u-mb-lg">
      * champs obligatoires
    </p>
    <MentionsLegalesNewsletter
      id="juridiqueNewsletter"
      class="c-inscription-form__notice u-mt-lg u-pb-lg u-anchor-has-sticky"
      info="¹"
    />
    <MentionsLegalesFidelite class="c-inscription-form__notice u-pb-lg" />
    <Popin
      ref="cgvPopin"
      aria-label="Conditions générales de vente"
      size="lg"
    >
      <CmsFetcher :link="cgvLink" />
      <div class="u-flex u-flex-justify-center">
        <Button
          variant="secondary"
          size="md"
          radius="full"
          class="u-m-sm"
          @click="$refs.cgvPopin.hide()"
        >
          OK
        </Button>
      </div>
    </Popin>
  </Form>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
.c-inscription-form .c-inscription-form__submit-error {
  max-width: 32rem;
}
</style>
